import { DECK_SIZES, deckSizeKey, type DeckSize } from "@fiveout/core";
import { XP_AWARDS } from "./levels";

/**
 * The local player profile: lifetime stats, unlocked achievements, and solo
 * high-score tables.
 *
 * Schema 3 exists to make cloud saves possible without an operation log: the
 * profile is shaped so that merging two of them is commutative, associative and
 * idempotent (docs/CLOUD_SAVES_DESIGN.md §3, LIVE_SERVICES_PLAN.md §B1-B4). Push
 * whole state, merge, and any order of syncs converges — no queue, no ordering,
 * no "who was newer". The local profile therefore carries exactly the cloud
 * document's shape plus the two device-scoped id lists that never sync.
 *
 * The per-field rules live in mergeProfiles.ts. The shape decisions here:
 *   - the 11 summed counters are per-install maps, and an install only ever
 *     writes its OWN key, so the merge is a per-key `max`. A scalar can't work:
 *     `max` loses progress (10 rounds on each of two devices reads as 10) and a
 *     sum inflates every time a sync is retried,
 *   - "best" values stay scalar and merge with `max`; the sets are key unions,
 *   - `xp` is derived from the counters and never merged (see recomputeXp).
 *
 * Schema 4 added `events`, and adds nothing else. It is a version bump rather
 * than a quiet extra field because of the §B5 gate: a schema-3 build merging a
 * cloud document copies across only the fields it knows, so letting one write
 * would drop a player's event record on the floor. Bumping the number turns
 * that build read-only against this document instead, until it is updated.
 *
 * Schema 5 added the Constellations counters, records and the set of events
 * seen, for the same reason: a schema-4 build merges only the counter keys it
 * knows, so it would write a document back without them.
 *
 * Schema 6 added `bigTableFlawlessWins` and the turn, lock and round records
 * behind the Wrecking Ball and Locksmith badges, for the same reason.
 *
 * Schema 7 added `thirdLocks` and `fourthLocks`, the counts behind the two
 * skull badges, again for the same reason: a schema-6 build reached dev.
 *
 * Schema 8 added `campaign`, each campaign level's first clear, for the same
 * reason as schema 4: a schema-7 build merging a cloud document copies only the
 * top-level fields it knows, so it would write the document back without the
 * campaign record - and with it the XP every clear pays.
 *
 * Schema 9 added `bestTurnsAlightInRound`, the record behind the Slow Burn
 * badge, for the same reason as schema 6: a schema-8 build merges only the
 * records it knows, so it would write the document back without it.
 *
 * Schema 10 added `bestWildKindsInTurn`, the record behind the Unicorn badge,
 * for the same reason: schema 9 had already reached dev.
 *
 * Schema 11 added `bestEventsInTurn`, the record behind the Alicorn frame, for
 * the same reason: schema 10 had already reached dev.
 *
 * Schema 12 added `campaignBest`, the most bonus objectives met in any cleared
 * round of a campaign level (the Starstone badge), for the same reason as
 * schema 8: a schema-11 build merging a cloud document copies only the
 * top-level fields it knows, so it would write the document back without it.
 *
 * Schema 13 added `mercyBursts`, the count behind the Baby Binky badge, for the
 * same reason as schema 7: a schema-12 build merges only the counter keys it
 * knows, so it would write the document back without it.
 */
export const PROFILE_SCHEMA_VERSION = 13;

export const PROFILE_KEY = "fiveout:profile";
const INSTALL_ID_KEY = "fiveout:installId";

/** The lifetime metrics that are summed across installs (§B2). */
export const COUNTER_KEYS = [
  "roundsPlayed",
  "roundsCompleted",
  "roundsWon",
  "fiveOuts",
  "offByOnes",
  "wildsPlayed",
  "bigSmashes",
  "missedFiveOuts",
  "missedOffByOnes",
  "flawlessWins",
  "comebackWins",
  // Schema 5: Constellations, and the two badges that came with it.
  "starsLit",
  "quadStars",
  "staircaseStars",
  "eventsTriggered",
  "bountyClaims",
  "bountyFiveOuts",
  "bountyOffByOnes",
  "swapTraps",
  "fiveOutGoOuts",
  "cometGoOuts",
  "cosmicCollisions",
  "stormWins",
  "starlessWins",
  "halfDeckStarWins",
  // Schema 6.
  "bigTableFlawlessWins",
  // Schema 7.
  "thirdLocks",
  "fourthLocks",
  // Schema 13.
  "mercyBursts",
] as const;

export type CounterKey = (typeof COUNTER_KEYS)[number];

/**
 * One metric's contributions: installId -> count, plus the reserved
 * `_archived` key holding everything the overflow guard has folded away (§B3).
 */
export type CounterMap = Record<string, number>;

/** Reserved counter key: the total contributed by installs that were folded away. */
export const ARCHIVED_INSTALL_KEY = "_archived";

/** Live install keys allowed per counter before the overflow fold runs (§B3). */
export const MAX_TRACKED_INSTALLS = 25;

/** One entry in a deck size's solo top-10 board. */
export interface SoloScore {
  /** Stable id, so a board merged from two devices can dedup (§B4). */
  id: string;
  turns: number;
  smash: boolean;
  lightning: boolean;
  /** Absent on runs recorded before Swap and Peek existed; the loader reads that as off. */
  swap: boolean;
  peek: boolean;
  playedAtMs: number;
}

/** What we know about an installation of the app. */
export interface InstallInfo {
  /** Epoch ms of that install's last save. Dates the overflow fold and `winStreak`. */
  lastActiveMs: number;
}

export interface LifetimeStats {
  /**
   * Lifetime experience points. Drives the player level (see levels.ts).
   * DERIVED — stored so readers stay cheap, but recomputed on every mutation and
   * every merge rather than accumulated. Never add to it directly.
   */
  xp: number;
  /**
   * The summed metrics, per install. Read them with `statTotal`, write them with
   * `bumpCounter` — nothing else may touch another install's key.
   *
   *   roundsPlayed     Rounds dealt. A refresh mid-round does not increment again.
   *   roundsCompleted  Rounds that reached a terminal phase.
   *   roundsWon        Solo rounds never count — a lone player always empties their deck.
   *   fiveOuts         Exact matches landed.
   *   offByOnes        Off-by-one matches landed.
   *   wildsPlayed      Wild cards played.
   *   bigSmashes       Smash Cards that annihilated 8 or more cards at once.
   *   missedFiveOuts   Turns where a Match guess would have paid off and the
   *   missedOffByOnes  player guessed something else. Coaching counters, not
   *                    achievements — new players almost never guess Match
   *                    because they don't realize how often it's there.
   *   flawlessWins     Rounds won without ever taking a stack.
   *   bigTableFlawlessWins  ...of them, online and dealt 20 or more cards.
   *   thirdLocks       Streaks that took another player from two locks to three.
   *   fourthLocks      ...and from three to four.
   *   mercyBursts      Mercy bursts this player was handed for falling behind, in online rounds.
   *   comebackWins     Rounds won after being last on the match table going in.
   *
   *   Constellations (stats/recorder.ts says exactly what each event is):
   *   starsLit         Stars this player lit on the Star Meter.
   *   quadStars        ...of them, lit by four of a kind.
   *   staircaseStars   ...of them, lit by a staircase.
   *   eventsTriggered  Events fired by the star this player lit.
   *   bountyClaims     Free cards claimed off a Bounty Pile.
   *   bountyFiveOuts   Five Outs on a Bounty Pile.
   *   bountyOffByOnes  Off-by-one matches on a Bounty Pile.
   *   cometGoOuts      Times a Comet Shower carried off the last card.
   *   cosmicCollisions Turns that both went out and fired an event.
   *   stormWins        Rounds won after a Card Storm or meteors hit the table.
   *   starlessWins     Rounds won with Constellations on and no star lit.
   *   halfDeckStarWins Half-deck rounds won with a star lit.
   *
   *   swapTraps        The next player took a pile whose top this player had
   *                    just swapped there.
   *   fiveOutGoOuts    Went out on a Five Out.
   */
  counters: Record<CounterKey, CounterMap>;
  /**
   * §B3 bookkeeping: metric -> installId -> the value that install held when it
   * was folded into `_archived`. Kept OUTSIDE `counters` so that map stays a
   * plain "installId -> count" that `statTotal` can sum without filtering, and
   * so the merge can tell a stale key for an already-archived install from an
   * install that has genuinely come back.
   */
  archivedBaselines: Record<CounterKey, CounterMap>;
  /** Current consecutive-round win streak (non-solo only). Live session state, not an accumulation. */
  winStreak: number;
  /** Best consecutive-round win streak. This is the one that has to survive a merge. */
  bestWinStreak: number;
  /**
   * Most cards laid in a row on one pile in a single turn: the played card,
   * duplicates, chain bonuses, a wild and the card laid back over it. Before
   * schema 6 it counted the chaining bonus's cards alone, so an old value is
   * never higher than the same turn would count now.
   */
  longestChain: number;
  /**
   * Most cards laid out of the deck in a single turn.
   *
   * Counts every card that left the deck onto the table that turn — the played
   * card, a Five Out's four, chained duplicates, the card laid back over a wild,
   * the one refilling a pile just taken. A big number is one turn where all of
   * those compounded, which is the thing worth a badge.
   */
  bestCardsInTurn: number;
  /** Highest number of Five Outs in a single round. */
  bestFiveOutsInRound: number;
  /** Highest number of off-by-one matches in a single round. */
  bestOffByOnesInRound: number;
  /** Most stars lit in a single round. */
  bestStarsInRound: number;
  /** Most stars lit by a single card coming to rest. */
  bestStarsInOneCard: number;
  /** Most cards one meteor event this player triggered took off the table. */
  bestMeteorDestroyed: number;
  /** Most cards destroyed in one turn: Smash, Lightning and the meteors this player's star fired. */
  bestDestroyedInTurn: number;
  /** Most different kinds of wild laid in a single turn: 4 is one of each, the Unicorn. */
  bestWildKindsInTurn: number;
  /** Most Constellations events set off in a single turn: 2 is the Alicorn. */
  bestEventsInTurn: number;
  /** Most of other players' piles locked by this player's streaks in a single round. */
  bestLocksInRound: number;
  /**
   * Most consecutive turns the deck stayed alight in a single online round: the
   * streak past the turns that light it, so a streak of 9 is 4. A run ends on a
   * spend or a taken pile.
   */
  bestTurnsAlightInRound: number;
  /** Constellations event kinds the player has seen fire, e.g. {"storm": true}. */
  starfallEventsSeen: Record<string, boolean>;
  /** AI difficulties beaten in a 1v1 round. */
  aiDefeated: Record<string, boolean>;
  /** Player counts a round has been played at, e.g. {"4": true}. */
  playerCountsPlayed: Record<string, boolean>;
  /**
   * Lobby sizes an ONLINE round has been won at, e.g. {"8": true}.
   *
   * A set rather than a best-so-far number because the sizes are not a ladder:
   * winning one-on-one is its own achievement, and a player who wins an
   * eight-hander first has not done it. In a party the key is the whole party's
   * roster — the lobby the round was won in — not the table it was split onto.
   */
  onlineWinTableSizes: Record<string, boolean>;
  /** Deck sizes a round has been played at, keyed by deckSizeKey. */
  deckSizesPlayed: Record<string, boolean>;
  /** Deck sizes a SOLO round has been completed at. */
  soloDeckSizesCompleted: Record<string, boolean>;
  /** Venue ids the player has played a round in. */
  venuesPlayed: Record<string, boolean>;
}

export interface PlayerProfile {
  schemaVersion: number;
  stats: LifetimeStats;
  /** achievementId -> epoch ms unlocked. */
  achievements: Record<string, number>;
  /**
   * Seasonal eventId -> epoch ms this player first took part (theme/events.ts).
   * Not achievements: an event pays no XP and is not a thing to complete.
   */
  events: Record<string, number>;
  /**
   * Campaign levelId -> epoch ms of its FIRST clear. A map of times rather than
   * a counter, like `events`: counters are summed across installs, so a level
   * cleared on two devices before they synced would pay twice. Keyed, the merge
   * is a union and a level is cleared once however many devices cleared it.
   */
  campaign: Record<string, number>;
  /**
   * Campaign levelId -> the most bonus objectives met in any one cleared round
   * of it (the Starstone badge's tiers). Its own map and NOT keys in `campaign`:
   * every entry there counts as a cleared level and pays its XP, here and in
   * Go's crest.XP, so a `best:` key would mint XP. Merged by max, which is safe
   * because a best only ever rises.
   */
  campaignBest: Record<string, number>;
  /** deckSizeKey -> ascending list of the best 10 solo turn counts. */
  soloScores: Record<string, SoloScore[]>;
  /** Every installation that has contributed, and when it last saved. */
  installs: Record<string, InstallInfo>;
  /** Epoch ms of the last merge. 0 until this profile has ever been merged. */
  mergedAtMs: number;
  /** Round ids already counted, so a browser refresh can't farm roundsPlayed. LOCAL ONLY. */
  countedRoundIds: string[];
  /** Round ids already counted as completed. LOCAL ONLY. */
  completedRoundIds: string[];
}

/** A profile as read back from storage or the cloud: any older schema, any field missing. */
export type StoredProfile = Omit<Partial<PlayerProfile>, "stats" | "soloScores"> & {
  /** Pre-v3 stats carried the counters as scalars alongside everything else. */
  stats?: Partial<LifetimeStats> & Partial<Record<CounterKey, number>>;
  soloScores?: Record<string, Partial<SoloScore>[]>;
};

let installId: string | null = null;

/**
 * This installation's id — the counter key this device is allowed to write.
 *
 * It identifies an *installation*, not a device: clearing storage or
 * reinstalling mints a new one. That is correct rather than a bug, because the
 * old install's contribution stays in the cloud and keeps counting (§B3).
 * Memoized: it is read on every counter bump.
 */
export function getInstallId(): string {
  if (installId) return installId;
  let stored: string | null = null;
  try {
    stored = localStorage.getItem(INSTALL_ID_KEY);
  } catch {
    /* no storage (tests, private mode) — fall through to a session-lifetime id */
  }
  installId = stored || newId();
  if (!stored) {
    try {
      localStorage.setItem(INSTALL_ID_KEY, installId);
    } catch {
      /* same */
    }
  }
  return installId;
}

/**
 * A UUID for install ids and solo entries. `crypto.randomUUID` needs a secure
 * context, which the LAN dev URLs and old browsers don't always give us, so a
 * uniqueness-is-good-enough fallback keeps ids from ever coming back undefined.
 */
function newId(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  }
}

/**
 * The id a pre-v3 solo entry gets, and the key the merge falls back to when an
 * entry has none. Deliberately the same string in both places: a board migrated
 * separately on two devices still dedups to one entry per run (§B4).
 */
export function naturalSoloId(entry: { turns: number; playedAtMs: number }): string {
  return `${entry.turns}:${entry.playedAtMs}`;
}

export function emptyCounters(): Record<CounterKey, CounterMap> {
  const out = {} as Record<CounterKey, CounterMap>;
  for (const key of COUNTER_KEYS) out[key] = {};
  return out;
}

function emptyStats(): LifetimeStats {
  return {
    xp: 0,
    counters: emptyCounters(),
    archivedBaselines: emptyCounters(),
    winStreak: 0,
    bestWinStreak: 0,
    longestChain: 0,
    bestCardsInTurn: 0,
    bestFiveOutsInRound: 0,
    bestOffByOnesInRound: 0,
    bestStarsInRound: 0,
    bestStarsInOneCard: 0,
    bestMeteorDestroyed: 0,
    bestDestroyedInTurn: 0,
    bestWildKindsInTurn: 0,
    bestEventsInTurn: 0,
    bestLocksInRound: 0,
    bestTurnsAlightInRound: 0,
    starfallEventsSeen: {},
    aiDefeated: {},
    playerCountsPlayed: {},
    onlineWinTableSizes: {},
    deckSizesPlayed: {},
    soloDeckSizesCompleted: {},
    venuesPlayed: {},
  };
}

export function emptyProfile(): PlayerProfile {
  const soloScores: Record<string, SoloScore[]> = {};
  for (const size of DECK_SIZES) soloScores[deckSizeKey(size)] = [];
  return {
    schemaVersion: PROFILE_SCHEMA_VERSION,
    stats: emptyStats(),
    achievements: {},
    events: {},
    campaign: {},
    campaignBest: {},
    soloScores,
    installs: {},
    mergedAtMs: 0,
    countedRoundIds: [],
    completedRoundIds: [],
  };
}

/** Lifetime total for a counter: every install's contribution, plus what was archived. */
export function statTotal(profile: PlayerProfile, key: CounterKey): number {
  let total = 0;
  for (const value of Object.values(profile.stats.counters[key] ?? {})) total += value;
  return total;
}

/**
 * Adds to THIS install's key. The only legal way to write a counter — writing
 * another install's key would break the per-key `max` merge, which is only safe
 * because each key has exactly one writer.
 */
export function bumpCounter(profile: PlayerProfile, key: CounterKey, n = 1): void {
  const id = getInstallId();
  const map = profile.stats.counters[key];
  map[id] = (map[id] ?? 0) + n;
}

/**
 * XP, from scratch.
 *
 * It is a pure function of three merged counters, the achievement count and the
 * campaign levels cleared, so merging summed counters AND separately merging the
 * xp derived from them would double count (§B2). Recompute instead — after every
 * mutation and every merge. profile.test.ts guards that every XP_AWARDS key
 * still has a derivable source; add an award that pays out on something else
 * and this rule quietly breaks.
 *
 * A campaign level pays a flat amount, not one per level, so the game server
 * (crest.XP, which checks a crest's level against the cloud save) can derive it
 * from the count alone without knowing the level list.
 */
export function recomputeXp(profile: PlayerProfile): number {
  return (
    statTotal(profile, "roundsCompleted") * XP_AWARDS.roundPlayed +
    statTotal(profile, "roundsWon") * XP_AWARDS.roundWon +
    statTotal(profile, "fiveOuts") * XP_AWARDS.perfectMatch +
    Object.keys(profile.achievements).length * XP_AWARDS.achievement +
    campaignLevelsCleared(profile) * XP_AWARDS.campaignLevel
  );
}

/** Campaign levels with a real first-clear time — what XP pays for, here and in Go's crest.XP. */
export function campaignLevelsCleared(profile: PlayerProfile): number {
  return Object.values(profile.campaign ?? {}).filter((at) => at > 0).length;
}

/** "Nothing to lose here" — what the first-sign-in conflict prompt keys off (§B7). */
export function isProfileEmpty(profile: PlayerProfile): boolean {
  if (Object.keys(profile.achievements).length > 0) return false;
  if (campaignLevelsCleared(profile) > 0) return false;
  for (const board of Object.values(profile.soloScores)) if (board.length > 0) return false;
  return COUNTER_KEYS.every((key) => statTotal(profile, key) === 0);
}

function num(value: unknown): number {
  return typeof value === "number" && Number.isFinite(value) ? value : 0;
}

function flags(raw: Record<string, boolean> | undefined): Record<string, boolean> {
  return { ...(raw ?? {}) };
}

/** An id -> epoch ms map, keeping only entries that are real times. */
function stamps(raw: Record<string, unknown> | undefined): Record<string, number> {
  const out: Record<string, number> = {};
  for (const [id, at] of Object.entries(raw ?? {})) if (num(at) > 0) out[id] = num(at);
  return out;
}

/** An id -> count map, keeping only entries that are real positive counts. */
function counts(raw: Record<string, unknown> | undefined): Record<string, number> {
  const out: Record<string, number> = {};
  for (const [id, n] of Object.entries(raw ?? {})) if (num(n) > 0) out[id] = num(n);
  return out;
}

/**
 * Fills in anything a stored profile is missing and upgrades older schemas, so
 * new fields never read undefined. Chained: a v1 profile comes out the far end
 * fully v3.
 *
 * `installId` and `nowMs` are parameters rather than reads so the upgrade can be
 * tested without a DOM.
 */
export function migrate(
  raw: StoredProfile | null,
  installIdForClaim: string = getInstallId(),
  nowMs: number = Date.now(),
): PlayerProfile {
  const base = emptyProfile();
  if (!raw) return base;

  const rawStats = raw.stats ?? {};
  const version = num(raw.schemaVersion) || 1;
  const stats = emptyStats();

  for (const key of COUNTER_KEYS) {
    const stored = rawStats.counters?.[key];
    if (stored && typeof stored === "object") {
      for (const [id, value] of Object.entries(stored)) stats.counters[key][id] = num(value);
    } else {
      // v2 -> v3: the scalar total is claimed by THIS install, because it is the
      // only one that has that history. Two devices migrating independently each
      // claim their own and the totals add — which is right, they really did
      // play both (§B5).
      const scalar = num(rawStats[key]);
      if (scalar !== 0) stats.counters[key][installIdForClaim] = scalar;
    }
    const baselines = rawStats.archivedBaselines?.[key];
    if (baselines && typeof baselines === "object") {
      for (const [id, value] of Object.entries(baselines)) stats.archivedBaselines[key][id] = num(value);
    }
  }

  stats.winStreak = num(rawStats.winStreak);
  stats.bestWinStreak = num(rawStats.bestWinStreak);
  stats.longestChain = num(rawStats.longestChain);
  stats.bestCardsInTurn = num(rawStats.bestCardsInTurn);
  stats.bestFiveOutsInRound = num(rawStats.bestFiveOutsInRound);
  stats.bestOffByOnesInRound = num(rawStats.bestOffByOnesInRound);
  stats.bestStarsInRound = num(rawStats.bestStarsInRound);
  stats.bestStarsInOneCard = num(rawStats.bestStarsInOneCard);
  stats.bestMeteorDestroyed = num(rawStats.bestMeteorDestroyed);
  stats.bestDestroyedInTurn = num(rawStats.bestDestroyedInTurn);
  stats.bestWildKindsInTurn = num(rawStats.bestWildKindsInTurn);
  stats.bestEventsInTurn = num(rawStats.bestEventsInTurn);
  stats.bestLocksInRound = num(rawStats.bestLocksInRound);
  stats.bestTurnsAlightInRound = num(rawStats.bestTurnsAlightInRound);
  stats.starfallEventsSeen = flags(rawStats.starfallEventsSeen);
  stats.aiDefeated = flags(rawStats.aiDefeated);
  stats.playerCountsPlayed = flags(rawStats.playerCountsPlayed);
  stats.onlineWinTableSizes = flags(rawStats.onlineWinTableSizes);
  stats.deckSizesPlayed = flags(rawStats.deckSizesPlayed);
  stats.soloDeckSizesCompleted = flags(rawStats.soloDeckSizesCompleted);
  stats.venuesPlayed = flags(rawStats.venuesPlayed);

  const soloScores: Record<string, SoloScore[]> = { ...base.soloScores };
  for (const [key, board] of Object.entries(raw.soloScores ?? {})) {
    soloScores[key] = (board ?? []).map((entry) => {
      const run = { turns: num(entry.turns), playedAtMs: num(entry.playedAtMs) };
      return {
        ...run,
        smash: !!entry.smash,
        lightning: !!entry.lightning,
        swap: !!entry.swap,
        peek: !!entry.peek,
        id: entry.id || naturalSoloId(run),
      };
    });
  }

  const installs: Record<string, InstallInfo> = {};
  for (const [id, info] of Object.entries(raw.installs ?? {})) {
    installs[id] = { lastActiveMs: num(info?.lastActiveMs) };
  }
  // Only the move to per-install counters (v3) claims history for this install;
  // v3 -> v4 added a field and moved nothing.
  if (version < 3) {
    // The whole pre-v3 history now sits under this install's key, so date it —
    // otherwise the fold and winStreak's LWW rule treat it as never active.
    installs[installIdForClaim] = {
      lastActiveMs: Math.max(installs[installIdForClaim]?.lastActiveMs ?? 0, nowMs),
    };
  }

  const profile: PlayerProfile = {
    schemaVersion: PROFILE_SCHEMA_VERSION,
    stats,
    achievements: { ...(raw.achievements ?? {}) },
    events: stamps(raw.events),
    campaign: stamps(raw.campaign),
    campaignBest: counts(raw.campaignBest),
    soloScores,
    installs,
    mergedAtMs: num(raw.mergedAtMs),
    // Bounded so the profile can't grow without limit over thousands of rounds.
    countedRoundIds: (raw.countedRoundIds ?? []).slice(-500),
    completedRoundIds: (raw.completedRoundIds ?? []).slice(-500),
  };

  // Schema 1 had no xp at all: levels used to be a pure function of achievements
  // unlocked, and rather than reset those players to level 1 the old v1 -> v2
  // step paid out retroactively from the counters already being kept. Now that
  // xp is derived, that backfill is simply what recomputing does, for everyone.
  profile.stats.xp = recomputeXp(profile);
  return profile;
}

let cached: PlayerProfile | null = null;

export function loadProfile(): PlayerProfile {
  if (cached) return cached;
  try {
    const raw = localStorage.getItem(PROFILE_KEY);
    cached = migrate(raw ? (JSON.parse(raw) as StoredProfile) : null);
  } catch {
    cached = emptyProfile();
  }
  return cached;
}

export function saveProfile(profile: PlayerProfile): void {
  // Stamped here, the one place every write funnels through, so this install's
  // activity date is always fresh for the merge's winStreak rule and the
  // overflow fold's least-recently-active choice.
  profile.installs[getInstallId()] = { lastActiveMs: Date.now() };
  cached = profile;
  try {
    localStorage.setItem(PROFILE_KEY, JSON.stringify(profile));
  } catch {
    /* storage full or blocked — stats are best-effort, never block play */
  }
  for (const listener of listeners) listener(profile);
}

const listeners = new Set<(p: PlayerProfile) => void>();

/** Subscribe to profile changes (used by the HUD level chip and achievements screen). */
export function onProfileChange(fn: (p: PlayerProfile) => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function cloneCounters(counters: Record<CounterKey, CounterMap>): Record<CounterKey, CounterMap> {
  const out = {} as Record<CounterKey, CounterMap>;
  for (const key of COUNTER_KEYS) out[key] = { ...(counters[key] ?? {}) };
  return out;
}

/** Read-modify-write helper. The mutator receives a deep-ish copy it may edit freely. */
export function updateProfile(mutate: (p: PlayerProfile) => void): PlayerProfile {
  const current = loadProfile();
  const next: PlayerProfile = {
    ...current,
    stats: {
      ...current.stats,
      counters: cloneCounters(current.stats.counters),
      archivedBaselines: cloneCounters(current.stats.archivedBaselines),
    },
    achievements: { ...current.achievements },
    events: { ...current.events },
    campaign: { ...current.campaign },
    campaignBest: { ...current.campaignBest },
    soloScores: { ...current.soloScores },
    installs: { ...current.installs },
    countedRoundIds: [...current.countedRoundIds],
    completedRoundIds: [...current.completedRoundIds],
  };
  mutate(next);
  // Every mutation, one place. Callers itemise what a round paid out for the
  // summary screen, but none of them add to the stored total.
  next.stats.xp = recomputeXp(next);
  saveProfile(next);
  return next;
}

export const SOLO_BOARD_SIZE = 10;

/**
 * Inserts a solo result into that deck size's top-10 board. Returns the 1-based
 * placement, or null if it didn't make the board.
 */
export function recordSoloScore(
  profile: PlayerProfile,
  size: DeckSize,
  entry: Omit<SoloScore, "id"> & { id?: string },
): number | null {
  const key = deckSizeKey(size);
  const scored: SoloScore = { ...entry, id: entry.id ?? newId() };
  const board = [...(profile.soloScores[key] ?? [])];
  board.push(scored);
  board.sort((a, b) => a.turns - b.turns || a.playedAtMs - b.playedAtMs);
  const trimmed = board.slice(0, SOLO_BOARD_SIZE);
  profile.soloScores[key] = trimmed;
  const idx = trimmed.indexOf(scored);
  return idx === -1 ? null : idx + 1;
}

/** Best (lowest) solo turn count for a deck size, or null if never completed. */
export function bestSoloTurns(profile: PlayerProfile, size: DeckSize): number | null {
  const board = profile.soloScores[deckSizeKey(size)] ?? [];
  return board.length ? board[0].turns : null;
}

/**
 * Forgets this device's copy without writing a replacement: account deletion's
 * "clear this device".
 *
 * Not `resetProfile`, which SAVES an empty profile and so notifies every
 * `onProfileChange` listener - cloud sync among them, which would answer by
 * pushing a blank save at an account that has just been deleted. Nothing is
 * notified here; the next `loadProfile` simply finds nothing and starts fresh.
 * The in-memory copy has to go too, or the next save writes it all back.
 */
export function discardStoredProfile(): void {
  cached = null;
  try {
    localStorage.removeItem(PROFILE_KEY);
  } catch {
    /* no storage: there was nothing stored to forget */
  }
}

/** Clears everything. Exposed for a "reset progress" control. */
export function resetProfile(): PlayerProfile {
  const fresh = emptyProfile();
  saveProfile(fresh);
  return fresh;
}

/** Everything that syncs. `xp` and the round-id lists are absent by design. */
export interface CloudProfileDoc {
  schemaVersion: number;
  stats: Omit<LifetimeStats, "xp">;
  achievements: Record<string, number>;
  events: Record<string, number>;
  campaign: Record<string, number>;
  campaignBest: Record<string, number>;
  soloScores: Record<string, SoloScore[]>;
  installs: Record<string, InstallInfo>;
  mergedAtMs: number;
}

/**
 * The sync engine's view of a profile.
 *
 * Three fields are dropped here rather than at the call site: `xp` because it is
 * derived and would double count (§B2), and the two round-id lists because they
 * are device-scoped by nature — round ids are minted locally, so the same id can
 * never appear on two devices, and leaving them out drops up to 1000 strings
 * from every sync payload.
 */
export function toCloudDoc(profile: PlayerProfile): CloudProfileDoc {
  const { xp: _derived, ...stats } = profile.stats;
  return {
    schemaVersion: profile.schemaVersion,
    stats: {
      ...stats,
      counters: cloneCounters(stats.counters),
      archivedBaselines: cloneCounters(stats.archivedBaselines),
      starfallEventsSeen: flags(stats.starfallEventsSeen),
      aiDefeated: flags(stats.aiDefeated),
      playerCountsPlayed: flags(stats.playerCountsPlayed),
      onlineWinTableSizes: flags(stats.onlineWinTableSizes),
      deckSizesPlayed: flags(stats.deckSizesPlayed),
      soloDeckSizesCompleted: flags(stats.soloDeckSizesCompleted),
      venuesPlayed: flags(stats.venuesPlayed),
    },
    achievements: { ...profile.achievements },
    events: { ...profile.events },
    campaign: { ...profile.campaign },
    campaignBest: { ...profile.campaignBest },
    soloScores: Object.fromEntries(Object.entries(profile.soloScores).map(([k, v]) => [k, v.map((e) => ({ ...e }))])),
    installs: Object.fromEntries(Object.entries(profile.installs).map(([k, v]) => [k, { ...v }])),
    mergedAtMs: profile.mergedAtMs,
  };
}

/**
 * Rebuilds a full profile from a cloud document: empty id lists (they never
 * synced), xp recomputed, older shapes migrated on read. The schema version the
 * cloud claimed is kept even if it is newer than this build understands — the
 * caller's version gate (§B5) decides what to do about that, and writing back a
 * lower number would be the silent downgrade the gate exists to prevent.
 */
export function fromCloudDoc(doc: Partial<CloudProfileDoc>): PlayerProfile {
  const profile = migrate(doc as StoredProfile);
  profile.schemaVersion = Math.max(profile.schemaVersion, num(doc.schemaVersion));
  return profile;
}
